import React from 'react';
import {
    SELFCARE_MATRIX_CRITICAL_THRESHOLD,
    SELFCARE_MATRIX_PROTECTIVE_THRESHOLD,
    getMatrixZone,
} from '../../pages/selfcare/selfcareReportLogic';

export { getMatrixZone };

const DEFAULT_SIZE = 190;

const clamp01 = (value) => {
    const v = Number(value);
    if (!Number.isFinite(v)) return 0;
    if (v < 0) return 0;
    if (v > 100) return 100;
    return v;
};

const scale = (value, minPx, maxPx) => {
    const v = clamp01(value);
    return minPx + (v / 100) * (maxPx - minPx);
};

const range = (start, end, step) => {
    const out = [];
    for (let i = start; i <= end; i += step) out.push(i);
    return out;
};

const PredictiveMatrix2x2Selfcare = ({
    xLabel,
    yLabel,
    xValue,
    yValue,
    size = DEFAULT_SIZE,
}) => {
    const paddingLeft = 28;
    const paddingRight = 10;
    const paddingTop = 10;
    const paddingBottom = 26;

    const w = size;
    const h = size;

    const plotLeft = paddingLeft;
    const plotRight = w - paddingRight;
    const plotTop = paddingTop;
    const plotBottom = h - paddingBottom;

    const zone = getMatrixZone(xValue, yValue);

    const xPx = scale(xValue, plotLeft, plotRight);
    const yPx = scale(100 - clamp01(yValue), plotTop, plotBottom); // SVG y aumenta hacia abajo

    const gridTicks = range(0, 100, 20);
    const boundaryLines = [SELFCARE_MATRIX_CRITICAL_THRESHOLD, SELFCARE_MATRIX_PROTECTIVE_THRESHOLD];

    const boundaryColor = '#1e88e5';
    const gridColor = '#d0d7de';
    const axisColor = '#222';

    return (
        <div style={{ width: w }}>
            <svg width={w} height={h} style={{ display: 'block' }}>
                {/* plot border */}
                <rect
                    x={plotLeft}
                    y={plotTop}
                    width={plotRight - plotLeft}
                    height={plotBottom - plotTop}
                    fill="#fff"
                    stroke={axisColor}
                    strokeWidth={1}
                />

                {/* grid */}
                {gridTicks.map((t) => {
                    const x = scale(t, plotLeft, plotRight);
                    const y = scale(100 - t, plotTop, plotBottom);
                    return (
                        <g key={t}>
                            <line x1={x} y1={plotTop} x2={x} y2={plotBottom} stroke={gridColor} strokeWidth={1} />
                            <line x1={plotLeft} y1={y} x2={plotRight} y2={y} stroke={gridColor} strokeWidth={1} />

                            <text x={x} y={plotBottom + 12} textAnchor="middle" fontSize={8} fill="#333">
                                {t}
                            </text>
                            <text x={plotLeft - 6} y={y + 3} textAnchor="end" fontSize={8} fill="#333">
                                {t}
                            </text>
                        </g>
                    );
                })}

                {/* boundary lines */}
                {boundaryLines.map((t) => {
                    const x = scale(t, plotLeft, plotRight);
                    const y = scale(100 - t, plotTop, plotBottom);
                    return (
                        <g key={`b-${t}`}>
                            <line x1={x} y1={plotTop} x2={x} y2={plotBottom} stroke={boundaryColor} strokeWidth={1.5} />
                            <line x1={plotLeft} y1={y} x2={plotRight} y2={y} stroke={boundaryColor} strokeWidth={1.5} />
                        </g>
                    );
                })}

                {/* point */}
                <circle cx={xPx} cy={yPx} r={4} fill="#0d47a1" />

                {/* axis labels */}
                <text x={(plotLeft + plotRight) / 2} y={h - 6} textAnchor="middle" fontSize={9} fill="#333">
                    {xLabel}
                </text>

                <text
                    x={10}
                    y={(plotTop + plotBottom) / 2}
                    transform={`rotate(-90 10 ${(plotTop + plotBottom) / 2})`}
                    textAnchor="middle"
                    fontSize={9}
                    fill="#333"
                >
                    {yLabel}
                </text>
            </svg>

            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 6 }}>
                <span
                    style={{
                        width: 10,
                        height: 10,
                        borderRadius: '50%',
                        display: 'inline-block',
                        backgroundColor: zone.color,
                    }}
                />
                <span style={{ fontSize: 10, color: '#6b7f95', fontWeight: 800 }}>{zone.label}</span>
            </div>
        </div>
    );
};

export default PredictiveMatrix2x2Selfcare;
